/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { FullModalLayout } from '@components/molecules'
import { mono, monoShared, monoForm } from '../monolith.tokens'

import { useAppSelector, useAppDispatch, customerAuthActions } from '@redux/index'
import { produce } from 'immer'

interface PropsType {
	onClose: () => void
}

/**
 * Change Password — My Info 안의 전체 화면 모달 (2026-10-06).
 * 예전 `/customer/change-password` 페이지의 필드 · 검증 · redux(UPDATE_PASSWORD) 그대로다.
 * 주소는 `/customer/info/password` — My Info 페이지가 연다. 저장이 성공하면(사가가 성공 모달을 켠다)
 * 이 모달은 닫히고 My Info 위에 성공 모달이 남는다.
 * 포털이라 페이지 스타일이 닿지 않아 폼 스타일을 여기서 다시 깐다.
 */
export const ChangePasswordModal = ({ onClose }: PropsType): JSX.Element => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const errorMessage: string = useAppSelector(
		state => state.customerAuth.updateCustomerPasswordErrorMessage,
	)
	const isUpdated: boolean = useAppSelector(
		state => state.customerAuth.showCustomerPasswordModal,
	)

	// state
	const [passwordInfo, setPasswordInfo] = React.useState({
		prevPassword: '',
		newPassword: '',
		confirmPassword: '',
	})

	// 열고 닫을 때 지난 오류 문구를 남기지 않는다
	React.useEffect(() => {
		reduxDispatch(customerAuthActions.UPDATE_CUSTOMER_PASSWORD_ERROR_MESSAGE(''))
		return () => {
			reduxDispatch(customerAuthActions.UPDATE_CUSTOMER_PASSWORD_ERROR_MESSAGE(''))
		}
	}, [])

	React.useEffect(() => {
		if (isUpdated) onClose()
	}, [isUpdated])

	const update = (patch: Partial<typeof passwordInfo>) => {
		setPasswordInfo(
			produce(passwordInfo, draft => {
				Object.assign(draft, patch)
			}),
		)
	}

	const onSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		reduxDispatch(customerAuthActions.UPDATE_PASSWORD(passwordInfo))
	}

	return (
		<FullModalLayout
			overlay
			size="small"
			title="Change Password"
			closeLabel="Close"
			onCloseClick={onClose}
		>
			<View css={style}>
				<form className="mnPasswordForm" onSubmit={onSubmit} noValidate>
					<View className="mnField">
						<label className="mnLabel" htmlFor="mnPwCurrent">
							Current password
						</label>
						<input
							id="mnPwCurrent"
							type="password"
							className={`mnInput ${errorMessage !== '' ? 'error' : ''}`}
							value={passwordInfo.prevPassword}
							onChange={e => update({ prevPassword: e.target.value })}
							autoComplete="current-password"
							autoFocus
						/>
					</View>

					<View className="mnField">
						<label className="mnLabel" htmlFor="mnPwNew">
							New password
						</label>
						<input
							id="mnPwNew"
							type="password"
							className="mnInput"
							value={passwordInfo.newPassword}
							onChange={e => update({ newPassword: e.target.value })}
							autoComplete="new-password"
						/>
					</View>

					<View className="mnField">
						<label className="mnLabel" htmlFor="mnPwConfirm">
							Confirm password
						</label>
						<input
							id="mnPwConfirm"
							type="password"
							className="mnInput"
							value={passwordInfo.confirmPassword}
							onChange={e => update({ confirmPassword: e.target.value })}
							autoComplete="new-password"
						/>
					</View>

					{errorMessage !== '' && <span className="mnError">{errorMessage}</span>}

					<View className="mnPasswordActions">
						<button type="submit" className="mnCta">
							Update
						</button>
						<button type="button" className="mnTextLink" onClick={onClose}>
							Cancel
						</button>
					</View>
				</form>
			</View>
		</FullModalLayout>
	)
}

const style = css({
	...monoShared,
	...monoForm,
	color: mono.ink,
	paddingBottom: 40,

	'.mnPasswordForm': {
		width: '100%',
		maxWidth: 420,
		display: 'flex',
		flexDirection: 'column',
		gap: 36,
		paddingTop: 8,
	},

	'.mnPasswordActions': {
		display: 'flex',
		alignItems: 'center',
		gap: 28,
		flexWrap: 'wrap',
		marginTop: 4,
	},
})
